const math = require('mathjs');
const _ = require('lodash');
/**
 * Makes an array of arrays of depths. Access by y index first, i.e. depths[y][x]
 * @param reading {SensorReading}
 * @return {(number|null)[][]}
 */
exports.depthArrays = (reading) => {
  const depthArray = Array(reading.height);
  for (let y = 0; y < reading.height; y++) {
    depthArray[y] = Array(reading.width);
    for (let x = 0; x < reading.width; x++) {
      const pixel = reading.getRaw(x, y);
      depthArray[y][x] = pixel.depthValid ? pixel.depth : null;
    }
  }
  return depthArray;
};

/**
 * Removes outlier points and replaces them with smoothed values
 * @param depths (number|null)[][]
 * @return (number|null)[][]
 */
exports.smoothDepths = (depths) => {
  const newDepths = _(depths).cloneDeep();
  const depthStdDeviation = _depthStdDeviation(depths);

  if (depthStdDeviation === null) {
    return newDepths;
  }

  // Remove outliers
  const height = depths.length;
  const width = depths[0].length;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const depthDifference = _averageDepthDiffToNeighbours(depths, x, y);
      if (depthDifference !== null && depthDifference > depthStdDeviation) {
        newDepths[y][x] = null;
      }
    }
  }

  let depthsInterpolatedThisRound;
  do {
    depthsInterpolatedThisRound = [];
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (newDepths[y][x] === null && depths[y][x] !== null) {
          const neighbourDepths = [];
          for (let yDiff = -1; yDiff <= 1; yDiff++) {
            for (let xDiff = -1; xDiff <= 1; xDiff++) {
              if (xDiff === 0 && yDiff === 0) {
                continue;
              }
              if ((y + yDiff < 0 || y + yDiff >= height || x + xDiff < 0 || x + xDiff >= width)) {
                // Not in bounds
                continue;
              }
              const neighbourDepth = newDepths[y + yDiff][x + xDiff];
              if (Number.isFinite(neighbourDepth)) {
                neighbourDepths.push(neighbourDepth);
              }
            }
          }
          if (neighbourDepths.length > 0) {
            depthsInterpolatedThisRound.push({ x, y, depth: math.mean(neighbourDepths) });
          }
        }
      }
    }
    // Apply interpolated depths for this round all at once so the order we interpolate pixels doesn't change the result
    for (const { x, y, depth } of depthsInterpolatedThisRound) {
      newDepths[y][x] = depth;
    }
  } while (depthsInterpolatedThisRound.length > 0);

  return newDepths;
};

function _averageDepthDiffToNeighbours(depthsArray, x, y) {
  const height = depthsArray.length;
  const width = depthsArray[0].length;

  const depth = depthsArray[y][x];
  if (!Number.isFinite(depth)) {
    return null;
  }

  const validNeighbourDepthDiffs = [];
  for (let yDiff = -1; yDiff <= 1; yDiff++) {
    for (let xDiff = -1; xDiff <= 1; xDiff++) {
      if (xDiff === 0 && yDiff === 0) {
        continue;
      }
      if ((y + yDiff < 0 || y + yDiff >= height || x + xDiff < 0 || x + xDiff >= width)) {
        // Not in bounds
        continue;
      }
      const neighbourDepth = depthsArray[y + yDiff][x + xDiff];
      if (Number.isFinite(neighbourDepth)) {
        validNeighbourDepthDiffs.push(depth - neighbourDepth);
      }
    }
  }
  if (validNeighbourDepthDiffs.length > 0) {
    return Math.abs(math.mean(validNeighbourDepthDiffs));
  }
  return null;
}

function _depthStdDeviation(depthsArray) {
  const depthDifferences = [];
  for (let y = 0; y < depthsArray.length; y++) {
    for (let x = 0; x < depthsArray[0].length; x++) {
      if (Number.isFinite(depthsArray[y][x])) {
        const depthDifference = _averageDepthDiffToNeighbours(depthsArray, x, y);
        if (depthDifference !== null) {
          depthDifferences.push(depthDifference);
        }
      }
    }
  }

  if (depthDifferences.length > 0) {
    return math.std(depthDifferences);
  }
  return null;
}
